import React, { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogContent, Typography, Button, Box } from "@mui/material";
import { billingActions, billingTriggerActions } from "../../../backend/store/actions/creators/billingActions";
import { UserContext } from "../../../contexts/UserContext";
import { hasPermission } from "../../../utils/permissions";
import logo from "../../../assets/images/logo-app.png";
import welcomeIcon from "../../../assets/images/WelcomeIcon.svg";
// import welcomeMap from "../../../assets/images/WelcomeIcon.svg";
import { useLocation } from "react-router-dom";
import { useNewFrameShown } from "../../../next/shell/useNewFrameShown";
import { useNextFrameMounted } from "../../../next/shell/frameMounted";
import { ONBOARDING_ROOT } from "../../../backend/sagas/onboardingRedirect";
import ConnectyidScreen from "../../../assets/images/ConnectyidScreen.jpg";
import {
    isWelcomeSplashDismissedLocally,
    markWelcomeSplashDismissedLocally,
} from "../../../utils/welcomeSplashDismissal";

const WelcomeSplash: React.FC = () => {
    const dispatch = useDispatch();
    const billing = useSelector((state: any) => state.billingStore);
    const workspaceId = useSelector((state: any) => state.authStore?.workspace?.id);
    const { user } = useContext(UserContext);
    const { pathname } = useLocation();
    // Never over the new frame (walkthrough 2026-10-29, issue 2): its pages say what they need to in place.
    const inNextFrame = useNextFrameMounted();
    const newFrameShown = useNewFrameShown();

    // Never over the signup flow. This component is mounted app-wide in
    // App/index.tsx, so it also renders on the onboarding route — and someone
    // who logged out mid-registration and signed back in landed on the step
    // they left, with this full-screen focus-trapping modal on top of it,
    // swallowing every click. Found by driving that exact journey in a
    // browser: the flow resumed correctly at "your-team" and then could not be
    // used at all.
    //
    // It is also the wrong message at that moment: a trial pitch aimed at
    // someone who has finished setting up, shown to someone still doing it.
    // The dashboard shows it the moment they arrive there instead.
    const inSignupFlow = pathname.startsWith(ONBOARDING_ROOT);

    const shouldShow = !inSignupFlow
        && !inNextFrame
        && !newFrameShown
        && billing.welcomeSplashSeen === false
        && !billing.hasActiveSubscription
        && billing.isSuperAdminWorkspace === false
        // Task 18 fix: this is a full-screen, focus-trapping MODAL over a
        // TRIAL/billing pitch ("Your N-day trial is now active!"). A
        // volunteer-only account (role `volunteer`, holds manage_volunteering
        // and nothing else -- no workspace, no subscription, no read_billing)
        // was getting it on first sign-in, blocking /volunteering behind a
        // billing wall on the one role whose entire app is explicitly
        // supposed to name no workspace, setting, or billing at all (see
        // VolunteeringPage's own doc comment). Gated on the same permission
        // the Billing sidebar item and route already require.
        && hasPermission("read_billing", user)
        // A dismissal already recorded locally (this click may still be in flight
        // on the server, e.g. blocked behind a contact-import lock — see
        // billingSaga's dismissWelcome comment) must keep the splash hidden even
        // before the server flag catches up, so a refresh mid-import can't bring
        // it back.
        && !isWelcomeSplashDismissedLocally(workspaceId);

    if (!shouldShow) return null;

    const handleGetStarted = () => {
        // Close instantly and synchronously — nothing here is awaited. The PUT
        // this triggers can be blocked for a long time (a contact import holds a
        // lock on the workspace row it updates), and this is a full-screen,
        // focus-trapping modal: waiting for the response before closing it would
        // leave the user stuck for the whole import.
        dispatch(billingActions.dismissWelcomeSuccess());
        markWelcomeSplashDismissedLocally(workspaceId);
        // Fire the server save in the background; billingSaga clears the local
        // marker once it actually succeeds, and authSaga retries it once on the
        // next app load if it hasn't by then.
        dispatch(billingTriggerActions.dismissWelcome());
    };

    return (
        <Dialog
            open={true}
            fullScreen
            sx={{ zIndex: 9999 }}
        >
            <DialogContent
                sx={{
                    p: 0,
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    overflow: "hidden",
                    backgroundColor: "white",
                }}
            >
                {/* Left Side: Content (Light Theme) */}
                <Box
                    sx={{
                        flex: { xs: "none", md: 1 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: { xs: "center", md: "flex-start" },
                        textAlign: { xs: "center", md: "left" },
                        p: { xs: 4, md: 10 },
                        backgroundColor: "white",
                        zIndex: 2,
                    }}
                >
                    <Box
                        component="img"
                        src={logo}
                        alt="ConnectYid"
                        sx={{
                            width: 220,
                            mb: 6,
                        }}
                    />

                    {billing.isTrialActive ? (
                        <>
                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 900,
                                    fontSize: { xs: "2.8rem", md: "4rem" },
                                    letterSpacing: "-0.03em",
                                    lineHeight: 1,
                                    color: "#0e0d5b",
                                    mb: 2,
                                }}
                            >
                                Your {billing.trialDays}-day trial is<br />
                                <span style={{ color: "#00d2ff" }}>now active!</span>
                            </Typography>

                            <Typography
                                variant="h5"
                                sx={{
                                    color: "#0e0d5b",
                                    fontWeight: 600,
                                    maxWidth: 500,
                                    lineHeight: 1.4,
                                    mb: 3,
                                    opacity: 0.9,
                                }}
                            >
                                Your trial has started — you have {billing.trialDays} full days to experience ConnectYid with your data.
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: "#666",
                                    fontSize: "1.1rem",
                                    maxWidth: 480,
                                    mb: 6,
                                    lineHeight: 1.6,
                                }}
                            >
                                Import complete! Your community data is loaded and ready. Explore routes, visits, and everything ConnectYid has to offer.
                            </Typography>

                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleGetStarted}
                                sx={{
                                    backgroundColor: "#00d2ff",
                                    color: "white",
                                    fontWeight: 800,
                                    px: 10,
                                    py: 2.5,
                                    fontSize: "1.2rem",
                                    borderRadius: "100px",
                                    textTransform: "none",
                                    boxShadow: "0 20px 40px rgba(0, 210, 255, 0.25)",
                                    "&:hover": {
                                        backgroundColor: "#00b8e6",
                                        transform: "translateY(-4px)",
                                        boxShadow: "0 25px 50px rgba(0, 210, 255, 0.35)",
                                    },
                                    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                                }}
                            >
                                Start Exploring
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 900,
                                    fontSize: { xs: "2.8rem", md: "4rem" },
                                    letterSpacing: "-0.03em",
                                    lineHeight: 1,
                                    color: "#0e0d5b",
                                    mb: 2,
                                }}
                            >
                                Welcome to<br />
                                <span style={{ color: "#00d2ff" }}>ConnectYid!</span>
                            </Typography>

                            <Typography
                                variant="h5"
                                sx={{
                                    color: "#0e0d5b",
                                    fontWeight: 600,
                                    maxWidth: 500,
                                    lineHeight: 1.4,
                                    mb: 3,
                                    opacity: 0.9,
                                }}
                            >
                                The ultimate operations platform for the modern Shliach.
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: "#666",
                                    fontSize: "1.1rem",
                                    maxWidth: 480,
                                    mb: 6,
                                    lineHeight: 1.6,
                                }}
                            >
                                Manage your community, plan outreach routes, and track every visit
                                with precision. Explore the system freely.
                            </Typography>

                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleGetStarted}
                                sx={{
                                    backgroundColor: "#00d2ff",
                                    color: "white",
                                    fontWeight: 800,
                                    px: 10,
                                    py: 2.5,
                                    fontSize: "1.2rem",
                                    borderRadius: "100px",
                                    textTransform: "none",
                                    boxShadow: "0 20px 40px rgba(0, 210, 255, 0.25)",
                                    "&:hover": {
                                        backgroundColor: "#00b8e6",
                                        transform: "translateY(-4px)",
                                        boxShadow: "0 25px 50px rgba(0, 210, 255, 0.35)",
                                    },
                                    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                                }}
                            >
                                Enter Dashboard
                            </Button>
                        </>
                    )}
                </Box>

                {/* Right Side: Visual Asset (Modern Gradient) */}
                <Box
                    sx={{
                        flex: 1.2,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundImage: `url(${ConnectyidScreen})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        overflow: "hidden",
                    }}
                >
                    {/* Subtle Background Map Overlay */}
                    <Box
                        component="img"
                        src={welcomeIcon}
                        sx={{
                            position: "absolute",
                            width: "78%",
                            opacity: 0.3,
                            pointerEvents: "none",
                            zIndex: 1,
                            filter: "grayscale(1) brightness(2)",
                            mixBlendMode: "overlay",
                        }}
                    />

                    {/* App Icon / Logo - Minimalist approach since the background has text */}
                    {/* <Box
                        sx={{
                            position: "relative",
                            width: "70%",
                            height: "60%",
                            borderRadius: "30px",
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            boxShadow: "0 40px 80px rgba(0,0,0,0.3)",
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 4
                        }}
                    >
                         <Box
                            component="img"
                            src={logo}
                            sx={{
                                width: "80%",
                                height: "auto",
                                filter: "brightness(0) invert(1)", // Make logo white
                                opacity: 0.9
                            }}
                        />
                    </Box> */}

                    {/* Branding Glow */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "100%",
                            height: "100%",
                            background: "radial-gradient(circle, rgba(0, 210, 255, 0.15) 0%, transparent 80%)",
                            zIndex: 1,
                        }}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default WelcomeSplash;



